/**
 * CXS MCP Settings popover.
 *
 * Lets the user configure CXS platform credentials (user ID, org ID)
 * and service_id (dashboard ID) for connecting to the CXS MCP server.
 * Values are persisted in a cookie via CxsMcpContext.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Settings2, X, Loader2 } from 'lucide-react';
import { useCxsMcp } from '../context/CxsMcpContext';

export function CxsMcpSettings() {
  const {
    sessionToken, serviceId, cxsUserId, cxsOrganizationId,
    setSessionToken, setServiceId, setCxsUserId, setCxsOrganizationId,
    mcpStatus, connectedTools,
  } = useCxsMcp();
  const [isOpen, setIsOpen] = useState(false);
  const [localToken, setLocalToken] = useState(sessionToken);
  const [localServiceId, setLocalServiceId] = useState(serviceId);
  const [localCxsUserId, setLocalCxsUserId] = useState(cxsUserId);
  const [localCxsOrgId, setLocalCxsOrgId] = useState(cxsOrganizationId);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Sync local state when context changes (e.g. initial load)
  useEffect(() => {
    setLocalToken(sessionToken);
    setLocalServiceId(serviceId);
    setLocalCxsUserId(cxsUserId);
    setLocalCxsOrgId(cxsOrganizationId);
  }, [sessionToken, serviceId, cxsUserId, cxsOrganizationId]);

  // Close popover on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleSave = () => {
    setSessionToken(localToken.trim());
    setServiceId(localServiceId.trim());
    setCxsUserId(localCxsUserId.trim());
    setCxsOrganizationId(localCxsOrgId.trim());
    setIsOpen(false);
  };

  const hasSettings = sessionToken || serviceId || cxsUserId || cxsOrganizationId;

  // Status line for inside the popover
  const statusLine =
    mcpStatus === 'connecting'
      ? 'Connecting to CXS MCP...'
      : mcpStatus === 'connected'
        ? `Connected (${connectedTools.length} tools)`
        : mcpStatus === 'error'
          ? 'Connection error'
          : hasSettings
            ? 'Not connected'
            : null;

  const statusColor =
    mcpStatus === 'connecting'
      ? 'text-amber-600 dark:text-amber-400'
      : mcpStatus === 'connected'
        ? 'text-green-600 dark:text-green-400'
        : mcpStatus === 'error'
          ? 'text-red-600 dark:text-red-400'
          : 'text-gray-400';

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg transition-colors ${
          isOpen
            ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
            : hasSettings
              ? 'bg-violet-50/50 dark:bg-violet-900/10 text-violet-500 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20'
              : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400'
        }`}
        title="CXS MCP Settings"
        data-testid="toggle-cxs-mcp-settings"
      >
        <Settings2 className="w-5 h-5" />
        {/* Status badge overlay */}
        {mcpStatus === 'connecting' && (
          <span className="absolute -top-0.5 -right-0.5">
            <Loader2 className="w-3 h-3 text-amber-500 animate-spin" />
          </span>
        )}
        {mcpStatus === 'connected' && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-900" />
        )}
        {mcpStatus === 'error' && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-gray-900" />
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-[100]" data-testid="cxs-mcp-popover">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">CXS MCP Settings</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 dark:text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Status line */}
          {statusLine && (
            <div className={`px-4 py-2 text-xs flex items-center gap-1.5 border-b border-gray-50 dark:border-gray-800 ${statusColor}`}>
              {mcpStatus === 'connecting' && <Loader2 className="w-3 h-3 animate-spin" />}
              {mcpStatus === 'connected' && <span className="w-2 h-2 bg-green-500 rounded-full" />}
              {mcpStatus === 'error' && <span className="w-2 h-2 bg-red-500 rounded-full" />}
              {statusLine}
            </div>
          )}

          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                CXS User ID
              </label>
              <input
                type="text"
                value={localCxsUserId}
                onChange={(e) => setLocalCxsUserId(e.target.value)}
                placeholder="qd74v9rvhndtcvmk..."
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                data-testid="cxs-user-id-input"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                CXS Organization ID
              </label>
              <input
                type="text"
                value={localCxsOrgId}
                onChange={(e) => setLocalCxsOrgId(e.target.value)}
                placeholder="m17fgj7d3b63kd9g..."
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                data-testid="cxs-organization-id-input"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Service ID
              </label>
              <input
                type="text"
                value={localServiceId}
                onChange={(e) => setLocalServiceId(e.target.value)}
                placeholder="nh7e9183njn84q2x..."
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                data-testid="cxs-service-id-input"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Session Token <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={localToken}
                onChange={(e) => setLocalToken(e.target.value)}
                placeholder="session_01KJ2WP5DZ..."
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                data-testid="cxs-session-token-input"
              />
            </div>

            <button
              onClick={handleSave}
              className="w-full py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
              data-testid="cxs-mcp-save-btn"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
